import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, storage } from '../firebase/config.js';
import {
  collection, query, where, getDocs, orderBy, doc,
  deleteDoc, updateDoc, Timestamp
} from 'firebase/firestore';
import {
  ref, uploadBytes, listAll, getDownloadURL, deleteObject
} from 'firebase/storage';
import { format, parseISO, isValid as isValidDate, isBefore, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { getAuth } from "firebase/auth";
import TenantConfigService from '../services/TenantConfigService.js';
import { isSchoolHoliday } from '../utils/scheduleUtils.js';

// Room display utility functions
const getRoomForPeriod = (jobData, periodId) => {
  // Check if this job uses multiple rooms
  if (jobData.useMultipleRooms && jobData.periodRooms) {
    return jobData.periodRooms[periodId] || 'Room not specified';
  } else {
    // Use the single room field
    return jobData.room || 'N/A';
  }
};

const getRoomSummary = (jobData) => {
  if (jobData.useMultipleRooms && jobData.periodRooms) {
    const roomEntries = Object.entries(jobData.periodRooms);
    if (roomEntries.length > 0) {
      return roomEntries.map(([periodId, room]) => `${periodId}: ${room}`).join(', ');
    }
    return 'Multiple rooms (see periods)';
  } else {
    return jobData.room || 'N/A';
  }
};

const usesMultipleRooms = (jobData) => {
  return Boolean(jobData.useMultipleRooms && jobData.periodRooms);
};

// Calendar Component for MyRoster
const MyRosterCalendar = ({ myRequests, myJobs, myAbsences, schoolHolidays, onDateClick, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get activities for a specific date
  const getActivitiesForDate = (date) => {
    const activities = [];
    const dateString = format(date, 'yyyy-MM-dd');
    
    // Check for holidays
    const holidayCheck = isSchoolHoliday(dateString, schoolHolidays);
    if (holidayCheck.isHoliday) {
      activities.push({
        type: 'holiday',
        status: 'holiday',
        holidayName: holidayCheck.holidayName,
        holidayType: holidayCheck.holidayType
      });
    }
    
    // Check absence requests (exclude cancelled and weekends)
    myAbsences.forEach(absence => {
      if (!absence.startDate || !absence.endDate) return;
      const startDate = parseISO(absence.startDate);
      const endDate = parseISO(absence.endDate);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
      
      if (date >= startDate && date <= endDate && absence.status !== 'cancelled' && !isWeekend) {
        activities.push({
          type: 'absence',
          status: absence.status,
          reason: absence.reason,
          reasonOther: absence.reasonOther,
          businessDaysCount: absence.businessDaysCount,
          fullAbsence: absence
        });
      }
    });
    
    // Check sub requests (created by this user)
    myRequests.forEach(request => {
      const requestDate = request.dateObj || (request.date && request.date !== 'N/A' ? parseISO(request.date) : null);
      if (requestDate && isSameDay(requestDate, date)) {
        request.periods?.forEach(period => {
          const isFilled = request.filled?.[period]?.name;
          activities.push({
            type: 'request',
            period: period,
            status: isFilled ? 'filled' : 'unfilled',
            teacher: request.name || 'You',
            department: request.department,
            room: getRoomForPeriod(request, period), // Use new room logic
            docId: request.docId,
            substituteInfo: isFilled ? request.filled[period] : null,
            fullRequest: request
          });
        });
      }
    });

    // Check subbing jobs (accepted by this user)
    myJobs.forEach(job => {
      const jobDate = job.dateObj || (job.date && job.date !== 'N/A' ? parseISO(job.date) : null);
      if (jobDate && isSameDay(jobDate, date)) {
        activities.push({
          type: 'job',
          period: job.period,
          status: 'accepted',
          teacher: job.teacher,
          department: job.department,
          room: getRoomForPeriod(job, job.period), // Use new room logic for job
          notes: job.notes,
          docId: job.docId,
          fullJob: job
        });
      }
    });

    return activities.sort((a, b) => {
      // Sort by priority: holidays first, then absences, then periods
      const typePriority = { holiday: 0, absence: 1, request: 2, job: 2 };
      const aPriority = typePriority[a.type] || 3;
      const bPriority = typePriority[b.type] || 3;
      
      if (aPriority !== bPriority) return aPriority - bPriority;
      
      // Within same type, sort periods numerically
      if (a.period && b.period) return Number(a.period) - Number(b.period);
      return 0;
    });
  };

  // Get the primary color for a date based on activities
  const getDateColor = (date) => {
    const activities = getActivitiesForDate(date);
    if (activities.length === 0) return 'white';
    
    // Priority: holidays > absences > accepted jobs > unfilled requests > filled requests
    const hasHoliday = activities.some(a => a.type === 'holiday');
    const hasAbsence = activities.some(a => a.type === 'absence');
    const hasAcceptedJob = activities.some(a => a.type === 'job');
    const hasUnfilledRequest = activities.some(a => a.type === 'request' && a.status === 'unfilled');
    const hasFilledRequest = activities.some(a => a.type === 'request' && a.status === 'filled');
    
    if (hasHoliday) return '#fef3cd'; // Light yellow
    if (hasAbsence) return '#e2e3f3'; // Light purple
    if (hasAcceptedJob) return '#fff3cd'; // Light orange
    if (hasUnfilledRequest) return '#f8d7da'; // Light red
    if (hasFilledRequest) return '#d4edda'; // Light green
    
    return 'white';
  };

  // Generate calendar grid with proper week alignment
  const generateCalendarGrid = () => {
    const firstDayOfWeek = getDay(monthStart);
    const grid = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      grid.push(null);
    }
    
    // Add all days of the month
    days.forEach(day => {
      grid.push(day);
    });
    
    return grid;
  };

  const calendarGrid = generateCalendarGrid();

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  return (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '20px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      {/* Calendar Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <button 
          onClick={goToPreviousMonth}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          ← Previous
        </button>
        <h3 style={{ margin: 0 }}>
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button 
          onClick={goToNextMonth}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          Next →
        </button>
      </div>

      {/* Day Labels */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '1px',
        marginBottom: '5px'
      }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ 
            padding: '10px', 
            textAlign: 'center', 
            fontWeight: 'bold',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6'
          }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '1px'
      }}>
        {calendarGrid.map((day, index) => {
          if (!day) {
            return (
              <div key={`empty-${index}`} style={{ 
                minHeight: '80px',
                border: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa'
              }} />
            );
          }

          const activitiesForDay = getActivitiesForDate(day);
          const dateColor = getDateColor(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());
          const isPastDay = isBefore(day, startOfDay(new Date()));

          return (
            <div
              key={day.toISOString()}
              onClick={() => onDateClick(day)}
              style={{
                minHeight: '80px',
                border: '2px solid ' + (isSelected ? '#007bff' : '#dee2e6'),
                padding: '5px',
                cursor: 'pointer',
                backgroundColor: dateColor,
                opacity: isPastDay ? 0.7 : 1,
                position: 'relative'
              }}
            >
              <div style={{ 
                fontWeight: isToday ? 'bold' : 'normal',
                color: isToday ? '#007bff' : isPastDay ? '#6c757d' : 'black',
                marginBottom: '3px'
              }}>
                {format(day, 'd')}
              </div>
              
              {/* Activity indicators */}
              {activitiesForDay.length > 0 && (
                <div style={{ fontSize: '10px' }}>
                  {activitiesForDay.slice(0, 4).map((activity, idx) => {
                    if (activity.type === 'holiday') {
                      return (
                        <div key={idx} style={{
                          backgroundColor: '#ffc107',
                          color: 'black',
                          borderRadius: '8px',
                          padding: '1px 4px',
                          margin: '1px 0',
                          fontSize: '8px',
                          textAlign: 'center',
                          fontWeight: 'bold'
                        }}>
                          Holiday
                        </div>
                      );
                    } else if (activity.type === 'absence') {
                      return (
                        <div key={idx} style={{
                          backgroundColor: activity.status === 'approved' ? '#6f42c1' : 
                                          activity.status === 'pending' ? '#17a2b8' : '#6c757d',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '1px 4px',
                          margin: '1px 0',
                          fontSize: '8px',
                          textAlign: 'center'
                        }}>
                          Absence
                        </div>
                      );
                    } else {
                      return (
                        <div key={idx} style={{
                          backgroundColor: activity.type === 'job' 
                            ? '#ff6b35' 
                            : activity.status === 'filled' 
                              ? '#28a745' 
                              : '#dc3545',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '1px 4px',
                          margin: '1px 0',
                          fontSize: '9px',
                          textAlign: 'center'
                        }}>
                          P{activity.period}
                        </div>
                      );
                    }
                  })}
                  {activitiesForDay.length > 4 && (
                    <div style={{
                      fontSize: '8px',
                      color: '#6c757d',
                      textAlign: 'center'
                    }}>
                      +{activitiesForDay.length - 4} more
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ marginTop: '15px', fontSize: '12px' }}>
        <h4 style={{ margin: '0 0 10px 0' }}>Legend:</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#fef3cd', 
              border: '1px solid #dee2e6' 
            }} />
            <span>School Holiday</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#e2e3f3', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Personal Absence</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#d4edda', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Sub Request Filled</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Sub Request Unfilled</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Accepted Subbing Job</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#ffc107'
            }} />
            <span>Holiday</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#6f42c1'
            }} />
            <span>Approved Absence</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#17a2b8'
            }} />
            <span>Pending Absence</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Day Details Component
const DayDetails = ({ selectedDate, myRequests, myJobs, myAbsences, schoolHolidays, handleEditButtonClick, handleDeleteRequest, saving, editingRequest, currentUser, canCreateRequests }) => {
  const getActivitiesForDate = (date) => {
    const activities = [];
    const dateString = format(date, 'yyyy-MM-dd');
    
    // Check for holidays
    const holidayCheck = isSchoolHoliday(dateString, schoolHolidays);
    if (holidayCheck.isHoliday) {
      activities.push({
        type: 'holiday',
        status: 'holiday',
        holidayName: holidayCheck.holidayName,
        holidayType: holidayCheck.holidayType
      });
    }
    
    // Check absence requests (exclude cancelled and weekends) - FIXED with null checks
    myAbsences.forEach(absence => {
      if (!absence.startDate || !absence.endDate) return; // Add null checks here
      
      const startDate = parseISO(absence.startDate);
      const endDate = parseISO(absence.endDate);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
      
      if (date >= startDate && date <= endDate && absence.status !== 'cancelled' && !isWeekend) {
        activities.push({
          type: 'absence',
          status: absence.status,
          reason: absence.reason,
          reasonOther: absence.reasonOther,
          businessDaysCount: absence.businessDaysCount,
          notes: absence.notes,
          fullAbsence: absence
        });
      }
    });
    
    // Check sub requests (created by this user)
    myRequests.forEach(request => {
      const requestDate = request.dateObj || (request.date && request.date !== 'N/A' ? parseISO(request.date) : null);
      if (requestDate && isSameDay(requestDate, date)) {
        request.periods?.forEach(period => {
          const isFilled = request.filled?.[period]?.name;
          activities.push({
            type: 'request',
            period: period,
            status: isFilled ? 'filled' : 'unfilled',
            teacher: request.name || 'You',
            department: request.department,
            room: getRoomForPeriod(request, period), // Use new room logic
            docId: request.docId,
            substituteInfo: isFilled ? request.filled[period] : null,
            fullRequest: request
          });
        });
      }
    });

    // Check subbing jobs (accepted by this user)
    myJobs.forEach(job => {
      const jobDate = job.dateObj || (job.date && job.date !== 'N/A' ? parseISO(job.date) : null);
      if (jobDate && isSameDay(jobDate, date)) {
        activities.push({
          type: 'job',
          period: job.period,
          status: 'accepted',
          teacher: job.teacher,
          department: job.department,
          room: getRoomForPeriod(job, job.period), // Use new room logic for job
          notes: job.notes,
          docId: job.docId,
          files: job.files,
          fullJob: job
        });
      }
    });

    return activities.sort((a, b) => {
      // Sort by priority: holidays first, then absences, then periods
      const typePriority = { holiday: 0, absence: 1, request: 2, job: 2 };
      const aPriority = typePriority[a.type] || 3;
      const bPriority = typePriority[b.type] || 3;
      
      if (aPriority !== bPriority) return aPriority - bPriority;
      
      // Within same type, sort periods numerically
      if (a.period && b.period) return Number(a.period) - Number(b.period);
      return 0;
    });
  };

  if (!selectedDate) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#6c757d',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa'
      }}>
        Click on a calendar date to view details for that day
      </div>
    );
  }

  const activities = getActivitiesForDate(selectedDate);

  if (activities.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#6c757d',
        border: '1px solid #dee2e6',
        borderRadius: '8px'
      }}>
        No activities scheduled for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
      </div>
    );
  }

  return (
    <div style={{ 
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '20px',
      backgroundColor: 'white'
    }}>
      <h3 style={{ 
        margin: '0 0 20px 0',
        color: '#007bff',
        borderBottom: '2px solid #007bff',
        paddingBottom: '10px'
      }}>
        Activities for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
      </h3>

      {activities.map((activity, index) => (
        <div key={`${activity.type}-${activity.docId || activity.holidayName || index}-${activity.period || ''}-${index}`} style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '15px',
          backgroundColor: activity.type === 'holiday' 
            ? '#fff8dc' 
            : activity.type === 'absence'
              ? '#f0f0ff'
              : activity.type === 'job' 
                ? '#fff8e1' 
                : activity.status === 'filled' 
                  ? '#e8f5e8' 
                  : '#ffebee'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '10px'
          }}>
            <h4 style={{ 
              margin: 0,
              color: activity.type === 'holiday'
                ? '#ffc107'
                : activity.type === 'absence'
                  ? activity.status === 'approved' ? '#6f42c1' : activity.status === 'pending' ? '#17a2b8' : '#6c757d'
                  : activity.type === 'job' 
                    ? '#ff6b35' 
                    : activity.status === 'filled' 
                      ? '#28a745' 
                      : '#dc3545'
            }}>
              {activity.type === 'holiday' && `🏖️ School Holiday`}
              {activity.type === 'absence' && (
                activity.status === 'approved' ? '✅ Personal Absence (Approved)' :
                activity.status === 'pending' ? '⏳ Personal Absence (Pending)' :
                activity.status === 'denied' ? '❌ Personal Absence (Denied)' :
                '📅 Personal Absence'
              )}
              {activity.type === 'job' && `🔸 Subbing Job - Period ${activity.period}`}
              {activity.type === 'request' && (
                activity.status === 'filled' 
                  ? `✅ Sub Request (Filled) - Period ${activity.period}`
                  : `❌ Sub Request (Unfilled) - Period ${activity.period}`
              )}
              
              {/* Show room for this specific period if using multiple rooms */}
              {activity.fullRequest && usesMultipleRooms(activity.fullRequest) && activity.period && (
                <span style={{ fontSize: '0.8em', color: '#6c757d', marginLeft: '10px' }}>
                  (Room: {activity.room})
                </span>
              )}
              {activity.fullJob && usesMultipleRooms(activity.fullJob) && activity.period && (
                <span style={{ fontSize: '0.8em', color: '#6c757d', marginLeft: '10px' }}>
                  (Room: {activity.room})
                </span>
              )}
            </h4>
            
            {activity.type === 'request' && canCreateRequests && (
              <div>
                <button 
                  onClick={() => handleEditButtonClick(activity.fullRequest)} 
                  style={{ 
                    marginRight: '5px', 
                    padding: '5px 10px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }} 
                  disabled={saving || !!editingRequest}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteRequest(activity.docId, format(selectedDate, 'MM/dd/yyyy'), activity.fullRequest.tenantId || currentUser?.tenantId)} 
                  style={{ 
                    padding: '5px 10px', 
                    backgroundColor: '#dc3545', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }} 
                  disabled={saving || !!editingRequest}
                >
                  Delete
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            {activity.type === 'holiday' && (
              <>
                <div><strong>Holiday:</strong> {activity.holidayName}</div>
                {activity.holidayType && <div><strong>Type:</strong> {activity.holidayType}</div>}
              </>
            )}
            
            {activity.type === 'absence' && (
              <>
                <div><strong>Reason:</strong> {activity.reason} {activity.reasonOther && `(${activity.reasonOther})`}</div>
                <div><strong>Status:</strong> {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}</div>
                {activity.businessDaysCount && (
                  <div><strong>Business Days:</strong> {activity.businessDaysCount}</div>
                )}
                {activity.notes && (
                  <div><strong>Notes:</strong> {activity.notes}</div>
                )}
              </>
            )}
            
            {(activity.type === 'request' || activity.type === 'job') && (
              <>
                <div><strong>Teacher:</strong> {activity.teacher}</div>
                <div><strong>Department:</strong> {activity.department}</div>
                <div><strong>Room:</strong> {activity.room}</div>
                
                {activity.type === 'request' && activity.substituteInfo && (
                  <div><strong>Substitute:</strong> {activity.substituteInfo.name}</div>
                )}
                
                {activity.type === 'job' && activity.notes && (
                  <div><strong>Notes:</strong> {activity.notes}</div>
                )}
              </>
            )}
          </div>

          {activity.type === 'job' && activity.files && activity.files.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <strong>Files:</strong>
              <ul style={{ margin: '5px 0 0 15px' }}>
                {activity.files.map((file, fileIdx) => (
                  <li key={fileIdx}>
                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                      {file.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// --- Edit Form Component ---
const EditRequestForm = ({ request, onSave, onCancel, isSaving, currentUser }) => {
    const [formData, setFormData] = useState({ 
      date: '', periods: '', reason: '', room: '', periodNotes: {}, 
      useMultipleRooms: false, periodRooms: {} 
    });
    const [existingFiles, setExistingFiles] = useState({});
    const [newFiles, setNewFiles] = useState({});
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);

    const tenantId = currentUser?.tenantId;

    useEffect(() => {
        if (request) {
            let dateStr = '';
            if (request.date?.toDate) { dateStr = format(request.date.toDate(), 'yyyy-MM-dd'); }
            else if (typeof request.date === 'string') { const p = parseISO(request.date); dateStr = isValidDate(p) ? format(p, 'yyyy-MM-dd') : request.date; }

            setFormData({
                date: dateStr,
                periods: (request.periods || []).join(', '),
                reason: request.reason || '',
                room: request.room || '',
                periodNotes: request.periodNotes || {},
                useMultipleRooms: request.useMultipleRooms || false,
                periodRooms: request.periodRooms || {}
            });
            setNewFiles({});
            setFilesToDelete([]);
            if (tenantId && request.docId) {
                fetchExistingFiles(request.docId, tenantId);
            }
        }
    }, [request, tenantId]);

    const fetchExistingFiles = async (requestId, currentTenantId) => {
        setLoadingFiles(true);
        setExistingFiles({});
        if (!currentTenantId) {
            console.warn("EditRequestForm: TenantId missing, cannot fetch files.");
            setLoadingFiles(false);
            return;
        }
        try {
            const listRef = ref(storage, `sub_files/${currentTenantId}/${requestId}/`);
            const res = await listAll(listRef);
            const filesData = {};
            for (const itemRef of res.items) {
                try {
                    const url = await getDownloadURL(itemRef);
                    const name = itemRef.name;
                    const match = name.match(/^period_(\d+)_/);
                    const period = match ? match[1] : 'general';

                    if (!filesData[period]) filesData[period] = [];
                    filesData[period].push({ name, url, ref: itemRef });
                } catch (urlError) {
                    console.error("Error getting download URL for", itemRef.name, urlError);
                }
            }
            setExistingFiles(filesData);
        } catch (error) { console.error("Error listing files:", error); }
        setLoadingFiles(false);
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleNoteChange = (p, v) => setFormData(prev => ({ ...prev, periodNotes: { ...prev.periodNotes, [p]: v } }));
    const handleFileChange = (p, f) => setNewFiles(prev => ({ ...prev, [p]: f }));
    
    // NEW: Handle period room changes
    const handlePeriodRoomChange = (period, room) => {
      setFormData(prev => ({ 
        ...prev, 
        periodRooms: { ...prev.periodRooms, [period]: room }
      }));
    };

    const handleDeleteExistingFile = (fileToDeleteRef) => {
        if (!window.confirm(`Are you sure you want to delete this file?`)) return;
        setFilesToDelete(prev => [...prev, fileToDeleteRef]);
        setExistingFiles(prev => {
            const updated = { ...prev };
            for (const period in updated) {
                updated[period] = updated[period].filter(f => f.ref.fullPath !== fileToDeleteRef.fullPath);
            }
            return updated;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const periodsArray = formData.periods.split(',').map(p => p.trim()).filter(p => p && !isNaN(p));
        if (!formData.date || !isValidDate(parseISO(formData.date))) { alert('Invalid date.'); return; }
        if (periodsArray.length === 0) { alert('Invalid periods.'); return; }

        const finalPeriodNotes = {};
        const finalPeriodRooms = {};
        periodsArray.forEach(p => { 
          finalPeriodNotes[p] = formData.periodNotes[p] || '';
          if (formData.useMultipleRooms) {
            finalPeriodRooms[p] = formData.periodRooms[p] || '';
          }
        });

        const updatedData = {
            date: formData.date,
            periods: periodsArray,
            reason: formData.reason,
            room: formData.useMultipleRooms ? '' : formData.room, // Clear single room if using multiple
            periodNotes: finalPeriodNotes,
            useMultipleRooms: formData.useMultipleRooms,
            periodRooms: formData.useMultipleRooms ? finalPeriodRooms : {} // Only save if using multiple rooms
        };
        onSave(request.docId, updatedData, newFiles, filesToDelete, tenantId);
    };

    const currentPeriods = formData.periods.split(',').map(p => p.trim()).filter(p => p && !isNaN(p));

    return (
        <div style={{ border: '1px solid #ccc', padding: '20px', margin: '20px 0', backgroundColor: '#f9f9f9' }}>
            <h3 style={{ marginTop: 0 }}>Editing Request ({formData.date})</h3>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '10px' }}><label>Date (YYYY-MM-DD):</label><br /><input type="date" name="date" value={formData.date} onChange={handleChange} required style={{ width: '98%', padding: '8px' }} /></div>
                <div style={{ marginBottom: '10px' }}><label>Periods (comma-separated):</label><br /><input type="text" name="periods" value={formData.periods} onChange={handleChange} required style={{ width: '98%', padding: '8px' }} /></div>
                <div style={{ marginBottom: '10px' }}><label>Reason:</label><br /><input type="text" name="reason" value={formData.reason} onChange={handleChange} style={{ width: '98%', padding: '8px' }} /></div>
                
                {/* Multiple Rooms Toggle */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={formData.useMultipleRooms}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          useMultipleRooms: e.target.checked,
                          room: e.target.checked ? '' : prev.room, // Clear single room if switching to multiple
                          periodRooms: e.target.checked ? prev.periodRooms : {} // Clear period rooms if switching to single
                        }));
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <span>Different room for each period</span>
                  </label>
                </div>

                {/* Single Room Input */}
                {!formData.useMultipleRooms && (
                  <div style={{ marginBottom: '10px' }}>
                    <label>Room:</label><br />
                    <input type="text" name="room" value={formData.room} onChange={handleChange} style={{ width: '98%', padding: '8px' }} />
                  </div>
                )}

                {/* Multiple Rooms Notice */}
                {formData.useMultipleRooms && (
                  <div style={{ 
                    marginBottom: '15px',
                    padding: '10px', 
                    backgroundColor: '#e7f3ff', 
                    border: '1px solid #b8daff', 
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#0c5460'
                  }}>
                    📍 Room numbers will be specified for each period below.
                  </div>
                )}

                {currentPeriods.length > 0 && (
                    <div style={{ marginTop: '15px', padding: '15px', border: '1px solid #ddd', borderRadius: '4px' }}>
                        <h4>Period Details & Files:</h4>
                        {currentPeriods.map(period => (
                            <div key={`edit_${period}`} style={{ borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '15px' }}>
                                <h5>Period {period}</h5>
                                
                                {/* Room input for each period when useMultipleRooms is true */}
                                {formData.useMultipleRooms && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <label>Room for Period {period}:</label><br />
                                    <input 
                                      type="text" 
                                      value={formData.periodRooms[period] || ''}
                                      onChange={(e) => handlePeriodRoomChange(period, e.target.value)}
                                      placeholder="e.g., 203B, Lab A, Gym"
                                      style={{ width: '98%', padding: '8px' }}
                                    />
                                  </div>
                                )}
                                
                                <label>Notes:</label><br />
                                <input type="text" value={formData.periodNotes[period] || ''} onChange={(e) => handleNoteChange(period, e.target.value)} style={{ width: '98%', padding: '8px', marginBottom: '10px' }} />
                                <label>Files:</label>
                                {loadingFiles ? <p>Loading files...</p> : (
                                    <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                                        {(existingFiles[period] || []).map((file, idx) => (
                                            <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                                <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name.replace(`period_${period}_`, '')}</a>
                                                <button type="button" onClick={() => handleDeleteExistingFile(file.ref)} style={{ marginLeft: '10px', padding: '2px 6px', color: 'red', cursor: 'pointer' }}>X</button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <label htmlFor={`file-edit-${period}`}>Upload New File for P{period}:</label><br />
                                <input type="file" id={`file-edit-${period}`} onChange={(e) => handleFileChange(period, e.target.files[0])} />
                                {newFiles[period] && <span style={{ marginLeft: '10px', fontSize: '0.9em', color: 'gray' }}>New: {newFiles[period].name}</span>}
                            </div>
                        ))}
                    </div>
                )}
                <button type="submit" disabled={isSaving} style={{ marginRight: '10px', padding: '10px 15px', marginTop: '15px' }}>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" onClick={onCancel} disabled={isSaving} style={{ padding: '10px 15px', marginTop: '15px' }}>
                    Cancel
                </button>
            </form>
        </div>
    );
};

const MyRoster = ({ currentUser }) => {
    const [myRequests, setMyRequests] = useState([]);
    const [myJobs, setMyJobs] = useState([]);
    const [myAbsences, setMyAbsences] = useState([]);
    const [schoolHolidays, setSchoolHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editingRequest, setEditingRequest] = useState(null);
    const [jobsSortBy, setJobsSortBy] = useState('date');
    const [viewMode, setViewMode] = useState('calendar'); // New state for view toggle
    const [selectedDate, setSelectedDate] = useState(null); // New state for selected calendar date
    
    const editRequestFormRef = useRef(null);

    const canCreateRequests = currentUser?.role === 'Faculty/Staff' || 
                              currentUser?.role === 'Staff' || 
                              currentUser?.hasElevatedPrivileges;

    const scrollToRef = (ref) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleEditButtonClick = (request) => {
        setEditingRequest(request);
        setTimeout(() => scrollToRef(editRequestFormRef), 50); 
    };

    // Handle calendar date click
    const handleDateClick = (date) => {
        setSelectedDate(date);
        if (viewMode === 'calendar') {
            // Stay in calendar view but show details below
        }
    };

    // Load tenant configuration including school holidays
    const loadTenantConfig = useCallback(async () => {
        if (!currentUser?.tenantId) {
            console.warn('MyRoster: Cannot load config, tenantId is missing.');
            return;
        }

        try {
            // Load school holidays
            const schoolHolidaysConfig = await TenantConfigService.getConfig(
                currentUser.tenantId, 
                'school_holidays', 
                { holidays: [] }
            );

            const holidays = schoolHolidaysConfig.holidays || [];
            setSchoolHolidays(holidays);

            console.log('✅ Loaded school holidays for MyRoster:', holidays);
        } catch (error) {
            console.error('Error loading tenant configuration for MyRoster:', error);
            setSchoolHolidays([]);
        }
    }, [currentUser?.tenantId]);

    // Sorting function for jobs
    const sortJobs = (jobs, sortBy) => {
        return [...jobs].sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    const dateA = a.dateObj || (a.date && a.date !== 'N/A' ? parseISO(a.date) : new Date(0));
                    const dateB = b.dateObj || (b.date && b.date !== 'N/A' ? parseISO(b.date) : new Date(0));
                    // Primary sort: date ascending, secondary sort: period ascending
                    const dateDiff = dateA - dateB;
                    if (dateDiff !== 0) return dateDiff;
                    return Number(a.period || 0) - Number(b.period || 0);
                    
                case 'teacher':
                    const teacherDiff = (a.teacher || '').localeCompare(b.teacher || '');
                    if (teacherDiff !== 0) return teacherDiff;
                    // Secondary sort by date if teachers are the same
                    const teacherDateA = a.dateObj || (a.date && a.date !== 'N/A' ? parseISO(a.date) : new Date(0));
                    const teacherDateB = b.dateObj || (b.date && b.date !== 'N/A' ? parseISO(a.date) : new Date(0));
                    return teacherDateA - teacherDateB;
                    
                case 'department':
                    const deptDiff = (a.department || '').localeCompare(b.department || '');
                    if (deptDiff !== 0) return deptDiff;
                    // Secondary sort by date if departments are the same
                    const deptDateA = a.dateObj || (a.date && a.date !== 'N/A' ? parseISO(a.date) : new Date(0));
                    const deptDateB = b.dateObj || (b.date && b.date !== 'N/A' ? parseISO(b.date) : new Date(0));
                    return deptDateA - deptDateB;
                    
                default:
                    return 0;
            }
        });
    };

    const fetchData = useCallback(async () => {
        if (!currentUser?.email || !currentUser?.tenantId) { 
            setError("User information or school/tenant context is not available."); 
            setLoading(false); 
            setMyRequests([]); 
            setMyJobs([]);
            setMyAbsences([]);
            return;
        }
        setLoading(true); 
        setError('');
        const userEmail = currentUser.email;
        const userTenantId = currentUser.tenantId; 

        try {
            // Load tenant config first
            await loadTenantConfig();

            // Fetch sub requests
            if (canCreateRequests) {
                const requestsQuery = query(
                    collection(db, "sub_requests"), 
                    where("requesterEmail", "==", userEmail),
                    where("tenantId", "==", userTenantId), 
                    orderBy("date", "desc")
                );
                const requestsSnapshot = await getDocs(requestsQuery);
                const fetchedRequests = requestsSnapshot.docs.map(d => {
                    const data = d.data();
                    const dateObj = data.date?.toDate ? data.date.toDate() : (typeof data.date === 'string' ? parseISO(data.date) : null);
                    return { 
                        docId: d.id, 
                        ...data, 
                        formattedDate: isValidDate(dateObj) ? format(dateObj, 'MM/dd/yyyy') : (data.date || 'N/A'),
                        dateObj 
                    };
                });
                setMyRequests(fetchedRequests);
            } else {
                setMyRequests([]);
            }

            // Fetch absence requests
            const absencesQuery = query(
                collection(db, "absence_requests"),
                where("requesterUid", "==", currentUser.uid),
                where("tenantId", "==", userTenantId),
                orderBy("createdAt", "desc")
            );
            const absencesSnapshot = await getDocs(absencesQuery);
            const fetchedAbsences = absencesSnapshot.docs.map(d => ({
                id: d.id,
                ...d.data(),
                // Convert Firestore timestamps to dates
                createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(d.data().createdAt),
                updatedAt: d.data().updatedAt?.toDate ? d.data().updatedAt.toDate() : new Date(d.data().updatedAt)
            }));
            setMyAbsences(fetchedAbsences);

            // Fetch subbing jobs
            const allTenantRequestsQuery = query(
                collection(db, "sub_requests"), 
                where("tenantId", "==", userTenantId), 
                orderBy("date", "desc") 
            );
            const allRequestsSnapshot = await getDocs(allTenantRequestsQuery);

            const jobsPromises = []; 

            allRequestsSnapshot.docs.forEach(d => {
                const request = { docId: d.id, ...d.data() };
                if (request.filled && Object.keys(request.filled).length > 0) {
                    Object.keys(request.filled).forEach(period => {
                        const filledInfo = request.filled[period];
                        if (filledInfo && filledInfo.email && filledInfo.email.toLowerCase() === userEmail.toLowerCase()) {
                            const dateObj = request.date?.toDate ? request.date.toDate() : (typeof request.date === 'string' ? parseISO(request.date) : null);
                            
                            const jobFilePromise = async () => {
                                const jobSpecificFiles = [];
                                try {
                                    const listRef = ref(storage, `sub_files/${userTenantId}/${request.docId}/`);
                                    const res = await listAll(listRef);
                                    for (const itemRef of res.items) {
                                        if (itemRef.name.startsWith(`period_${period}_`)) {
                                            try {
                                                const url = await getDownloadURL(itemRef);
                                                jobSpecificFiles.push({ name: itemRef.name.replace(`period_${period}_`, ''), url });
                                            } catch (urlError) {
                                                console.error(`Error getting URL for ${itemRef.name}:`, urlError);
                                            }
                                        }
                                    }
                                } catch (listError) {
                                     console.error(`Error listing files for request ${request.docId}, period ${period} (tenant ${userTenantId}):`, listError);
                                }
                                return {
                                    docId: d.id, 
                                    period: period,
                                    date: isValidDate(dateObj) ? format(dateObj, 'MM/dd/yyyy') : (request.date || 'N/A'),
                                    teacher: request.name, 
                                    department: request.department,
                                    notes: request.periodNotes?.[period] || 'N/A', 
                                    room: request.room || 'N/A',
                                    files: jobSpecificFiles,
                                    dateObj,
                                    // NEW: Include room data for proper display
                                    useMultipleRooms: request.useMultipleRooms,
                                    periodRooms: request.periodRooms || {},
                                    // Keep full request data for room calculations
                                    fullRequestData: request
                                };
                            };
                            jobsPromises.push(jobFilePromise());
                        }
                    });
                }
            });

            const fetchedJobs = await Promise.all(jobsPromises);
            setMyJobs(fetchedJobs);

        } catch (err) {
            console.error("MyRoster Error fetching data:", err);
            setError(`Failed to fetch data: ${err.message}`);
        }
        setLoading(false);
    }, [currentUser, canCreateRequests, loadTenantConfig]);

    useEffect(() => { 
        if (currentUser && currentUser.email && currentUser.tenantId) { 
            fetchData();
        } else {
            setLoading(false);
            setMyRequests([]);
            setMyJobs([]);
            setMyAbsences([]);
            if (currentUser) { 
                setError("User information is incomplete. Cannot load roster.");
            }
        }
    }, [fetchData, currentUser]); 

    const handleDeleteRequest = async (requestId, requestDate, requestTenantId) => { 
        if (!window.confirm(`Are you sure you want to delete your sub request for ${requestDate}? This will also delete any associated files and cannot be undone.`)) return;
        if (!requestTenantId) {
            alert("Cannot delete request: tenant information missing.");
            return;
        }
        setSaving(true);
        try {
            const listRef = ref(storage, `sub_files/${requestTenantId}/${requestId}/`);
            const res = await listAll(listRef);
            await Promise.all(res.items.map(itemRef => deleteObject(itemRef)));
            
            await deleteDoc(doc(db, "sub_requests", requestId));
            alert("Request deleted successfully.");
            fetchData(); 
        } catch (err) { 
            console.error("Error deleting request:", err); 
            alert(`Failed to delete request: ${err.message}`); 
        }
        setSaving(false);
     };

    const handleSaveEdit = async (requestId, updatedData, newFiles, filesToDelete, requestTenantId) => { 
        if (!requestTenantId) {
            alert("Cannot save request: tenant information missing.");
            return;
        }
        setSaving(true);
        try {
            await Promise.all(filesToDelete.map(fileRef => deleteObject(fileRef)));

            for (const period in newFiles) {
                const file = newFiles[period];
                if (file) {
                    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                    const filePath = `sub_files/${requestTenantId}/${requestId}/period_${period}_${safeFileName}`;
                    const storageRef = ref(storage, filePath);
                    await uploadBytes(storageRef, file);
                }
            }
            const requestDocRef = doc(db, "sub_requests", requestId);
            const { tenantId, ...dataToUpdate } = updatedData; 
            await updateDoc(requestDocRef, { ...dataToUpdate, timestamp: Timestamp.now() });

            alert("Request updated successfully!");
            setEditingRequest(null);
            fetchData(); 
        } catch (err) { 
            console.error("Error saving request:", err);
            alert(`Failed to save changes: ${err.message}`); 
        }
        setSaving(false);
     };

    const calculateAnalytics = () => { 
        const totalRequests = myRequests.length;
        const upcomingRequests = myRequests.filter(r => {
             try { 
                 const dateToCompare = r.dateObj || (r.formattedDate && r.formattedDate !== 'N/A' ? parseISO(r.formattedDate) : (r.date ? parseISO(r.date) : null) );
                 return dateToCompare && isValidDate(dateToCompare) && !isBefore(startOfDay(dateToCompare), startOfDay(new Date())); 
                }
             catch(e){ return false; }
        }).length;
        const totalJobs = myJobs.length;
         const upcomingJobs = myJobs.filter(j => {
             try { 
                 const dateToCompare = j.dateObj || (j.date && j.date !== 'N/A' ? parseISO(j.date) : null);
                 return dateToCompare && isValidDate(dateToCompare) && !isBefore(startOfDay(dateToCompare), startOfDay(new Date()));
                }
             catch(e){ return false; }
        }).length;
        const totalAbsences = myAbsences.length;
        const pendingAbsences = myAbsences.filter(a => a.status === 'pending').length;
        const approvedAbsences = myAbsences.filter(a => a.status === 'approved').length;
        return { totalRequests, upcomingRequests, totalJobs, upcomingJobs, totalAbsences, pendingAbsences, approvedAbsences };
    };

    const analytics = calculateAnalytics(); 
    const sortedJobs = sortJobs(myJobs, jobsSortBy);

    if (loading) return <p style={{ padding: '20px' }}>Loading My Relief Roster...</p>;
    if (error) return <p style={{ padding: '20px', color: 'red' }}>Error: {error}</p>;

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <h1>My Relief Roster for {currentUser?.displayName || currentUser?.name || 'User'}</h1>
            {editingRequest && canCreateRequests && (
                <div ref={editRequestFormRef}> 
                    <EditRequestForm
                        request={editingRequest}
                        onSave={(docId, data, newF, filesToDel) => handleSaveEdit(docId, data, newF, filesToDel, editingRequest.tenantId || currentUser?.tenantId)} 
                        onCancel={() => setEditingRequest(null)}
                        isSaving={saving}
                        currentUser={currentUser} 
                    />
                </div>
            )}

            {/* View Toggle Buttons */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                    onClick={() => setViewMode('calendar')}
                    style={{
                        backgroundColor: viewMode === 'calendar' ? '#007bff' : '#6c757d',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                    }}
                >
                    📅 Calendar View
                </button>
                <button
                    onClick={() => setViewMode('analytics')}
                    style={{
                        backgroundColor: viewMode === 'analytics' ? '#007bff' : '#6c757d',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                    }}
                >
                    📊 Analytics & Tables
                </button>
            </div>

            {viewMode === 'calendar' ? (
                <div>
                    <MyRosterCalendar 
                        myRequests={myRequests}
                        myJobs={myJobs}
                        myAbsences={myAbsences}
                        schoolHolidays={schoolHolidays}
                        onDateClick={handleDateClick}
                        selectedDate={selectedDate}
                    />
                    
                    <div style={{ marginTop: '20px' }}>
                        <DayDetails 
                            selectedDate={selectedDate}
                            myRequests={myRequests}
                            myJobs={myJobs}
                            myAbsences={myAbsences}
                            schoolHolidays={schoolHolidays}
                            handleEditButtonClick={handleEditButtonClick}
                            handleDeleteRequest={handleDeleteRequest}
                            saving={saving}
                            editingRequest={editingRequest}
                            currentUser={currentUser}
                            canCreateRequests={canCreateRequests}
                        />
                    </div>
                </div>
            ) : (
                <div>
                    <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #eee', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                        <h2>My Analytics</h2>
                        <ul>
                            {canCreateRequests && (
                                <li>Total Sub Requests Made: <strong>{analytics.totalRequests}</strong> (Upcoming: {analytics.upcomingRequests})</li>
                            )}
                            <li>Total Subbing Jobs Accepted: <strong>{analytics.totalJobs}</strong> (Upcoming: {analytics.upcomingJobs})</li>
                            <li>Total Absence Requests: <strong>{analytics.totalAbsences}</strong> (Pending: {analytics.pendingAbsences}, Approved: {analytics.approvedAbsences})</li>
                        </ul>
                    </section>
                    
                    {/* Absence Requests Table */}
                    <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <h2>My Absence Requests</h2>
                        {myAbsences.length === 0 ? <p>You have not made any absence requests.</p> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                               <thead>
                                    <tr style={{backgroundColor: '#f2f2f2'}}>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Date Range</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Reason</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Days</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Status</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Submitted</th>
                                    </tr>
                               </thead>
                               <tbody>
                                   {myAbsences.map(absence => (
                                       <tr key={absence.id}>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                             {absence.startDate === absence.endDate 
                                               ? format(parseISO(absence.startDate), 'MMM d, yyyy')
                                               : `${format(parseISO(absence.startDate), 'MMM d')} - ${format(parseISO(absence.endDate), 'MMM d, yyyy')}`
                                             }
                                           </td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                             {absence.reason}
                                             {absence.reasonOther && (
                                               <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                 {absence.reasonOther}
                                               </div>
                                             )}
                                           </td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                             {absence.businessDaysCount || 'N/A'}
                                           </td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                             <span style={{
                                               padding: '2px 8px',
                                               borderRadius: '12px',
                                               fontSize: '12px',
                                               fontWeight: 'bold',
                                               backgroundColor: absence.status === 'approved' ? '#d4edda' :
                                                              absence.status === 'pending' ? '#fff3cd' :
                                                              absence.status === 'denied' ? '#f8d7da' : '#e2e3e5',
                                               color: absence.status === 'approved' ? '#155724' :
                                                     absence.status === 'pending' ? '#856404' :
                                                     absence.status === 'denied' ? '#721c24' : '#6c757d'
                                             }}>
                                               {absence.status.charAt(0).toUpperCase() + absence.status.slice(1)}
                                             </span>
                                           </td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                             {absence.createdAt ? format(absence.createdAt, 'MMM d, yyyy') : 'Unknown'}
                                           </td>
                                       </tr>
                                   ))}
                               </tbody>
                            </table>
                        )}
                    </section>
                    
                    {canCreateRequests && (
                        <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #eee', borderRadius: '8px' }}>
                            <h2>My Sub Requests</h2>
                            {myRequests.length === 0 ? <p>You have not made any sub requests.</p> : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                   <thead>
                                        <tr style={{backgroundColor: '#f2f2f2'}}>
                                            <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Date</th>
                                            <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Dept.</th>
                                            <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Room(s)</th>
                                            <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Periods (Filled By)</th>
                                            <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Actions</th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                       {myRequests.map(req => (
                                           <tr key={req.docId}>
                                               <td style={{border: '1px solid #ddd', padding: '8px'}}>{req.formattedDate}</td>
                                               <td style={{border: '1px solid #ddd', padding: '8px'}}>{req.department}</td>
                                               <td style={{border: '1px solid #ddd', padding: '8px'}}>{getRoomSummary(req)}</td>
                                               <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                                   {(req.periods || []).sort((a,b) => Number(a) - Number(b)).map(p => {
                                                     const roomDisplay = usesMultipleRooms(req) ? ` (${getRoomForPeriod(req, p)})` : '';
                                                     return (
                                                       <div key={p}>P{p}{roomDisplay}: {req.filled?.[p]?.name || <span style={{color:'orange'}}>Needed</span>}</div>
                                                     );
                                                   })}
                                               </td>
                                               <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                                   <button onClick={() => handleEditButtonClick(req)} style={{ marginRight: '5px', padding: '5px 8px' }} disabled={saving || !!editingRequest}>Edit</button>
                                                   <button onClick={() => handleDeleteRequest(req.docId, req.formattedDate, req.tenantId || currentUser?.tenantId)} style={{ padding: '5px 8px', backgroundColor: '#d9534f', color: 'white' }} disabled={saving || !!editingRequest}>Delete</button>
                                               </td>
                                           </tr>
                                       ))}
                                   </tbody>
                                </table>
                            )}
                        </section>
                    )}

                    <section style={{ marginBottom: '30px', padding: '20px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h2 style={{ margin: 0 }}>My Subbing Jobs</h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label htmlFor="sort-select" style={{ fontSize: '0.9em', fontWeight: 'bold' }}>Sort by:</label>
                                <select 
                                    id="sort-select"
                                    value={jobsSortBy} 
                                    onChange={(e) => setJobsSortBy(e.target.value)}
                                    style={{ 
                                        padding: '5px 10px', 
                                        borderRadius: '4px', 
                                        border: '1px solid #ccc',
                                        fontSize: '0.9em'
                                    }}
                                >
                                    <option value="date">Date</option>
                                    <option value="teacher">Teacher</option>
                                    <option value="department">Department</option>
                                </select>
                            </div>
                        </div>
                        
                        {myJobs.length === 0 ? <p>You have not accepted any subbing jobs.</p> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                               <thead>
                                   <tr style={{backgroundColor: '#f2f2f2'}}>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Date</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Teacher</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Dept.</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Period</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Room</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Notes</th>
                                        <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Attached Files</th>
                                   </tr>
                               </thead>
                               <tbody>
                                   {sortedJobs.map((job, index) => (
                                       <tr key={`${job.docId}-${job.period}-${index}`}>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>{job.date}</td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>{job.teacher}</td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>{job.department}</td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>{job.period}</td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>{getRoomForPeriod(job.fullRequestData || job, job.period)}</td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>{job.notes}</td>
                                           <td style={{border: '1px solid #ddd', padding: '8px'}}>
                                               {job.files && job.files.length > 0 ? (
                                                   <ul style={{ margin: 0, paddingLeft: '15px' }}>
                                                       {job.files.map((file, fileIdx) => (
                                                           <li key={fileIdx}>
                                                               <a href={file.url} target="_blank" rel="noopener noreferrer">
                                                                   {file.name}
                                                               </a>
                                                           </li>
                                                       ))}
                                                   </ul>
                                               ) : (
                                                   <span>N/A</span>
                                               )}
                                           </td>
                                       </tr>
                                   ))}
                               </tbody>
                            </table>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default MyRoster;